import React, { useState, useEffect } from "react";
import { 
  CircularProgress, 
  TablePagination
} from "@mui/material";
import { Star, User, MessageSquare } from "lucide-react";
import Layout from "../../components/layout/Layout";
import feedbacksApi from "../../api/feedbacks";

const Feedbacks: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchFeedbacks();
  }, [page, rowsPerPage]);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const data = await feedbacksApi.getAllFeedbacks(page + 1, rowsPerPage);
      setFeedbacks(data.feedbacks || []);
      setTotal(data.pagination.total);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Star className="w-6 h-6 mr-3 text-indigo-600 fill-current" /> Customer Feedbacks
            </h1>
            <p className="text-gray-500 mt-1">View all meal ratings and reviews from your customers.</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading && feedbacks.length === 0 ? (
            <div className="flex justify-center p-12"><CircularProgress /></div>
          ) : feedbacks.length > 0 ? (
            <>
              {/* Desktop View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Meal</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Rating</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Feedback</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {feedbacks.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {item.userId?.profileImage ? (
                              <img src={item.userId.profileImage} alt="" className="w-8 h-8 rounded-full border border-gray-200 object-cover mr-3" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 mr-3">
                                <User className="w-4 h-4" />
                              </div>
                            )}
                            <span className="text-sm font-medium text-gray-900">{item.userId?.name || "Unknown User"}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            {item.mealId?.imageUrl && (
                              <img src={item.mealId.imageUrl} alt="" className="w-10 h-10 rounded-md object-cover mr-3 border border-gray-100" />
                            )}
                            <span className="text-sm font-bold text-gray-900">{item.mealId?.name || "Unknown Meal"}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.mealRating ? (
                            <div className="flex items-center bg-yellow-50 px-2.5 py-1 rounded-md border border-yellow-100 w-fit">
                              <Star className="w-4 h-4 text-yellow-500 fill-current mr-1.5" />
                              <span className="text-sm font-bold text-yellow-700">{item.mealRating}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No Rating</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">
                          {item.feedback ? (
                            <p className="line-clamp-2 italic text-gray-600">"{item.feedback}"</p>
                          ) : (
                            <span className="text-xs text-gray-400">No Feedback Text</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile View */}
              <div className="block md:hidden p-4 space-y-4 bg-gray-50/50">
                {feedbacks.map((item, index) => (
                  <div key={`mobile-${index}`} className="p-5 bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center">
                        {item.userId?.profileImage ? (
                          <img src={item.userId.profileImage} alt="" className="w-10 h-10 rounded-full border border-gray-200 object-cover mr-3" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 mr-3">
                            <User className="w-5 h-5" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-bold text-gray-900">{item.userId?.name || "Unknown User"}</p>
                          <p className="text-xs text-gray-500">{new Date(item.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                      {item.mealRating && (
                        <div className="flex items-center bg-yellow-50 px-2 py-1 rounded-md border border-yellow-100">
                          <Star className="w-4 h-4 text-yellow-500 fill-current mr-1" />
                          <span className="text-sm font-bold text-yellow-700">{item.mealRating}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="mb-3 flex items-center bg-gray-50/80 p-2.5 rounded-lg border border-gray-100">
                      {item.mealId?.imageUrl && (
                        <img src={item.mealId.imageUrl} alt="" className="w-8 h-8 rounded-md object-cover mr-3 border border-gray-200" />
                      )}
                      <span className="text-xs font-bold text-gray-800">{item.mealId?.name || "Unknown Meal"}</span>
                    </div>

                    {item.feedback && (
                      <div className="bg-indigo-50/50 p-3 rounded-lg border border-indigo-50">
                        <p className="text-sm text-gray-700 italic">"{item.feedback}"</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          ) : (
            <div className="text-center py-20">
              <MessageSquare className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Feedbacks Found</h3>
              <p className="text-gray-500">Your customers haven't submitted any meal ratings or reviews yet.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Feedbacks;
